import React, { useEffect, useState, useRef } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { InputTextarea } from 'primereact/inputtextarea';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import axiosInstance from '../services/axiosInstance';
import AIGrnUsage from '../components/AIGrnUsage';


const OrganizationSettingsPage = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);
    const printlblRef = useRef(null);
    const approvalsRef = useRef(null);
    const operatorauthRef = useRef(null);
    const notificationsRef = useRef(null);
    const expiryRef = useRef(null);
    const brandingRef = useRef(null);
    const smsRef = useRef(null);
    const fitmeQrRef = useRef(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await axiosInstance.get('/api/orgsettings/');
            if (res.data.length > 0) {
                // ensure backwards-compatibility: default allow_exceptional_inventory = true
                const s = res.data[0];
                s.custom_json_settings = s.custom_json_settings || {};
                // --- PRINT SETTINGS DEFAULTS ---
                s.custom_json_settings.printing = s.custom_json_settings.printing || {};
                const p = s.custom_json_settings.printing;
                // --- BRANDING DEFAULTS ---
                s.custom_json_settings.branding = s.custom_json_settings.branding || {};
                // --- OPERATOR AUTH DEFAULTS ---
                s.custom_json_settings.operator_authentication = s.custom_json_settings.operator_authentication || {};
                
                s.custom_json_settings.sms = s.custom_json_settings.sms || {};

                const sms = s.custom_json_settings.sms;
                if (!sms.templates) sms.templates = {};

                const ba = s.custom_json_settings.branding;
                if (!ba.theme) ba.theme = "fluent-light";

                const oa = s.custom_json_settings.operator_authentication;
                if (typeof oa.enabled === 'undefined') oa.enabled = false;
                if (!Array.isArray(oa.methods)) oa.methods = ['PIN'];
                if (typeof oa.allow_user_pin_change === 'undefined') oa.allow_user_pin_change = true;

                

                const b = s.custom_json_settings.branding;
                if (typeof b.show_in_app_header === 'undefined') b.show_in_app_header = true;
                if (typeof b.show_in_invoice === 'undefined') b.show_in_invoice = false;

                if (!p.receipt_language) p.receipt_language = 'escpos'; // default
                if (!p.paper_width_mm) p.paper_width_mm = 80;
                if (!p.receipt_height_mm) p.receipt_height_mm = 300;
                if (typeof p.receipt_enabled === 'undefined') p.receipt_enabled = true;
                if (typeof p.label_enabled === 'undefined') p.label_enabled = false;
                if (!p.receipt_header) {
                    p.receipt_header = [
                        { key: 'pharmacy_name', enabled: true,  order: 1 },
                        { key: 'logo',          enabled: false, order: 2 },
                        { key: 'address',       enabled: true,  order: 3 },
                        { key: 'contact',       enabled: true,  order: 4 },
                        { key: 'department',    enabled: true,  order: 5 },
                        { key: 'date',          enabled: true,  order: 6 },
                        { key: 'operator',      enabled: true,  order: 7 },
                        { key: 'customer',      enabled: true,  order: 8 }
                    ];
                }
                if (!p.receipt_footer_text) {
                    p.receipt_footer_text = 'Thank you for choosing us!';
                }
                if (typeof p.po_barcode_enabled === 'undefined') p.po_barcode_enabled = false;
                if (!p.po_barcode_value) p.po_barcode_value = 'batch_number'; // batch_number | product_code
                if (!p.po_barcode_copies) p.po_barcode_copies = 1;
                if (!p.label_width_mm) p.label_width_mm = 50;
                if (!p.label_height_mm) p.label_height_mm = 30;
                if (!p.label_margin_mm) p.label_margin_mm = 4;

                if (typeof s.custom_json_settings.allow_exceptional_inventory === 'undefined') {
                    s.custom_json_settings.allow_exceptional_inventory = false;
                }
                if (typeof s.custom_json_settings.refill_reminder_days === 'undefined') {
                    s.custom_json_settings.refill_reminder_days = 2; // default: 2 days before refill
                }
                if (typeof s.require_approval_for_purchases === 'undefined') {
                    s.require_approval_for_purchases = true;
                }
                if (typeof s.require_delete_move_order_approval === 'undefined') {
                    s.require_delete_move_order_approval = true;
                }
                if (typeof s.require_transfer_move_order_approval === 'undefined') {
                    s.require_transfer_move_order_approval = true;
                }
                if (typeof s.allow_direct_grn_auto_approve === 'undefined') {
                    s.allow_direct_grn_auto_approve = false;
                }
                if (typeof s.allow_self_approve === 'undefined') {
                    s.allow_self_approve = false;
                }
                // expose a top-level convenience property for binding in UI (optional)
                s.allow_exceptional_inventory = s.custom_json_settings.allow_exceptional_inventory;
                setSettings(s);
             }
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const getBranding = () => {
        return settings?.custom_json_settings?.branding || {};
    };

    const updateBranding = (key, value) => {
        setSettings(prev => {
            const css = { ...(prev.custom_json_settings || {}) };
            const branding = { ...(css.branding || {}) };
            branding[key] = value;
            css.branding = branding;
            return { ...prev, custom_json_settings: css };
        });
    };

    const handleOperatorAuthChange = (key, value) => {
        setSettings(prev => {
            const css = { ...(prev.custom_json_settings || {}) };
            const oa = { ...(css.operator_authentication || {}) };
            oa[key] = value;
            css.operator_authentication = oa;
            return { ...prev, custom_json_settings: css };
        });
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('logo', file);

        try {
            const res = await axiosInstance.post(
                '/api/orgsettings/upload-logo/',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            updateBranding('logo_url', res.data.logo_url);
            handleApiSuccess('Logo uploaded successfully');
        } catch (err) {
            handleApiError(err);
        }
    };

    const [qrDownloading, setQrDownloading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

    useEffect(() => {
        axiosInstance.get('/api/departments/')
            .then(res => setDepartments(res.data || []))
            .catch(() => {}); // non-fatal — org-level QR still works without branch list
    }, []);

    const handleDownloadQrCode = async () => {
        setQrDownloading(true);
        try {
            // responseType 'blob' — this endpoint returns a raw PNG, not JSON,
            // so it needs to bypass the usual axios JSON parsing.
            const url = selectedDepartmentId
                ? `/api/orgsettings/qr-code/?department_id=${selectedDepartmentId}`
                : '/api/orgsettings/qr-code/';
            const res = await axiosInstance.get(url, {
                responseType: 'blob',
            });

            const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            const branchLabel = selectedDepartmentId
                ? departments.find(d => d.id === selectedDepartmentId)?.department
                : null;
            link.download = branchLabel
                ? `fitme-registration-qr-${branchLabel}.png`
                : 'fitme-registration-qr.png';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            handleApiSuccess('QR code downloaded');
        } catch (err) {
            handleApiError(err);
        } finally {
            setQrDownloading(false);
        }
    };

    const handleChange = (field, value) => {
        if (field.startsWith('printing.')) {
            const key = field.replace('printing.', '');
            setSettings(prev => {
                const css = { ...(prev.custom_json_settings || {}) };
                const printing = { ...(css.printing || {}) };
                printing[key] = value;
                css.printing = printing;
                return { ...prev, custom_json_settings: css };
            });
            return;
        }
        if (field === 'allow_exceptional_inventory') {
            // update inside custom_json_settings
            setSettings(prev => {
                const css = { ...(prev.custom_json_settings || {}) };
                css.allow_exceptional_inventory = !!value;
                return { ...prev, custom_json_settings: css, allow_exceptional_inventory: !!value };
            });
            return;
        }
        if (field === 'require_approval_for_purchases' || field === 'allow_direct_grn_auto_approve' || field === 'allow_self_approve') {
            setSettings(prev => ({ ...prev, [field]: !!value }));
            return;
        }
        if (field === 'require_delete_move_order_approval' || field === 'require_transfer_move_order_approval') {
            setSettings(prev => ({ ...prev, [field]: !!value }));
            return;
        }
        if (field === 'refill_reminder_days') {
            setSettings(prev => {
                const css = { ...(prev.custom_json_settings || {}) };
                css.refill_reminder_days = Number.isFinite(value) ? value : 0;
                return { ...prev, custom_json_settings: css };
            });
            return;
        }
        setSettings((prev) => ({ ...prev, [field]: value }));
    };

    const getSmsSettings = () => settings?.custom_json_settings?.sms || {};
    
    // Credentials & request_template live inside custom_json_settings.sms
    const handleSmsCredentialChange = (key, value) => {
        setSettings(prev => {
            const css = { ...(prev.custom_json_settings || {}) };
            const sms = { ...(css.sms || {}) };
            sms[key] = value;
            css.sms = sms;
            return { ...prev, custom_json_settings: css };
        });
    };
    
    // Message templates live in custom_json_settings.sms.templates
    const handleSmsTemplateChange = (templateKey, value) => {
        setSettings(prev => {
            const css = { ...(prev.custom_json_settings || {}) };
            const sms = { ...(css.sms || {}) };
            const templates = { ...(sms.templates || {}) };
            templates[templateKey] = value;
            sms.templates = templates;
            css.sms = sms;
            return { ...prev, custom_json_settings: css };
        });
    };
    
    // Toggle booleans and sms_sender_name/sms_bearer_token are top-level model fields
    const handleSmsToggleChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };
    
    const handleTestSms = async () => {
        const phone = prompt('Enter phone number for test SMS (e.g. +94771234567):');
        if (!phone) return;
        try {
            await axiosInstance.post(`/api/orgsettings/${settings.id}/test-sms/`, { phone });
            handleApiSuccess('Test SMS sent successfully.');
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleSubmit = async () => {
        try {
            const BEARER_MASK = '••••••';
            const payload = { ...settings };
            const token = payload.sms_bearer_token || '';
            if (token === BEARER_MASK) {
                // User didn't touch the token field — don't send it, backend preserves existing
                delete payload.sms_bearer_token;
            } else if (token === '') {
                // User explicitly cleared the field — signal backend to clear it
                payload.sms_bearer_token = '__CLEAR__';
            }
            // Otherwise a new real value was typed — send it through
            await axiosInstance.put(`/api/orgsettings/${settings.id}/`, payload);
            localStorage.setItem('organizationSettings', JSON.stringify(settings));
            handleApiSuccess('Settings updated successfully.');
        } catch (error) {
            handleApiError(error);
        }
    };

    const scrollToRef = (ref) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (loading || !settings) return <p>Loading settings...</p>;

    const printing = {receipt_header: [], receipt_footer_text: '', ...settings.custom_json_settings.printing};

    return (
        <div className="grid">
            {/* Left Navigation Pane */}
            <div className="col-fixed" style={{ width: '200px' }}>
                <div className="p-3 surface-card border-round shadow-1" style={{ position: 'sticky', top: '1rem' }}>
                    <h6 className="mb-3">Settings</h6>

                    <Button icon="pi pi-crown" label="Branding" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(brandingRef)} />

                    <Button icon="pi pi-qrcode" label="FitMe QR Code" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(fitmeQrRef)} />

                    <Button icon="pi pi-check-square" label="Approvals" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(approvalsRef)} />

                    <Button icon="pi pi-user" label="Invoice Operator" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(operatorauthRef)} />

                    <Button icon="pi pi-calendar-times" label="Expiry Rules" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(expiryRef)} />

                    <Button icon="pi pi-bell" label="Notifications" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(notificationsRef)} />
    
                    <Button icon="pi pi-send" label="SMS Notifications" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(smsRef)} />

                    <Button icon="pi pi-print" label="Invoice Printing" className="p-button-text w-full text-left mb-2"
                        onClick={() => scrollToRef(printRef)} />

                    <Button icon="pi pi-tags" label="Label Printing" className="p-button-text w-full text-left"
                        onClick={() => scrollToRef(printlblRef)} />
                    
                    <div className="flex justify-content-end mt-5">
                        <Button
                            label="Save Settings"
                            icon="pi pi-save"
                        className="p-button-primary"
                            onClick={handleSubmit}
                        />
                    </div>
                    </div>
            </div>

            {/* Settings Content */}
            <div className="col">
                
                    <h3 className="mb-4">Organization Settings</h3>
                    <AIGrnUsage />
                    <section ref={brandingRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5>Branding</h5>

                        <div className="flex gap-4 align-items-start">
                           <div>
                            <label>Pharmacy Logo</label>

                            {getBranding().logo_url && (
                                <div className="mb-2">
                                    <img
                                        src={getBranding().logo_url}
                                        alt="Pharmacy Logo"
                                        style={{ maxHeight: '80px', objectFit: 'contain' }}
                                        className="border-1 surface-border p-2 border-round"
                                    />
                                </div>
                            )}

                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={handleLogoUpload}
                            />

                            <small className="text-color-secondary">
                                Recommended: PNG, transparent background, max height 100px
                            </small>
                        </div>
                        </div>

                        <div className="field">
                            <div className="flex align-items-center gap-2 mb-2">
                                <Checkbox
                                    checked={!!getBranding().show_in_app_header}
                                    onChange={(e) =>
                                        updateBranding('show_in_app_header', e.checked)
                                    }
                                />
                                <label className="ml-2 mb-0">
                                    Show logo in application header
                                </label>
                            </div>
                        </div>

                        <div className="field">
                            <div className="flex align-items-center gap-2 mb-2">
                                <Checkbox
                                    checked={!!getBranding().show_in_invoice}
                                    onChange={(e) =>
                                        updateBranding('show_in_invoice', e.checked)
                                    }
                                />
                                <label className="ml-2 mb-0">
                                    Show logo in invoice / receipt
                                </label>
                            </div>
                        </div>
                        <div className="field col-12 md:col-4">
                            <label>Application Theme</label>
                            <select
                                className="p-inputtext"
                                value={getBranding().theme || "fluent-light"}
                                onChange={(e) => updateBranding("theme", e.target.value)}
                                style={{ maxWidth: "250px" }}
                            >
                                <option value="arya-blue">Arya Blue</option>
                                <option value="arya-green">Arya Green</option>
                                <option value="arya-orange">Arya Orange</option>
                                <option value="arya-purple">Arya Purple</option>
                                <option value="bootstrap4-dark-blue">Bootstrap Dark Blue</option>
                                <option value="bootstrap4-dark-purple">Bootstrap Dark Purple</option>
                                <option value="bootstrap4-light-blue">Bootstrap Light Blue</option>
                                <option value="bootstrap4-light-purple">Bootstrap Light Purple</option>
                                <option value="fluent-light">Fluent Light</option>
                                <option value="lara-dark-amber">Lara Dark Amber</option>
                                <option value="lara-dark-blue">Lara Dark Blue</option>
                                <option value="lara-dark-cyan">Lara Dark Cyan</option>
                                <option value="lara-dark-green">Lara Dark Green</option>
                                <option value="lara-dark-indigo">Lara Dark Indigo</option>
                                <option value="lara-dark-pink">Lara Dark Pink</option>
                                <option value="lara-dark-purple">Lara Dark Purple</option>
                                <option value="lara-dark-teal">Lara Dark Teal</option>
                                <option value="lara-light-amber">Lara Light Amber</option>
                                <option value="lara-light-blue">Lara Light Blue</option>
                                <option value="lara-light-cyan">Lara Light Cyan</option>
                                <option value="lara-light-green">Lara Light Green</option>
                                <option value="lara-light-indigo">Lara Light Indigo</option>
                                <option value="lara-light-pink">Lara Light Pink</option>
                                <option value="lara-light-purple">Lara Light Purple</option>
                                <option value="lara-light-teal">Lara Light Teal</option>
                                <option value="luna-amber">Luna Amber</option>
                                <option value="luna-blue">Luna Blue</option>
                                <option value="luna-green">Luna Green</option>
                                <option value="luna-pink">Luna Pink</option>
                                <option value="md-dark-deeppurple">Md Dark Deeppurple</option>
                                <option value="md-dark-indigo">Md Dark Indigo</option>
                                <option value="md-light-deeppurple">Md Light Deeppurple</option>
                                <option value="md-light-indigo">Md Light Indigo</option>
                                <option value="mdc-dark-deeppurple">Mdc Dark Deeppurple</option>
                                <option value="mdc-dark-indigo">Mdc Dark Indigo</option>
                                <option value="mdc-light-deeppurple">Mdc Light Deeppurple</option>
                                <option value="mdc-light-indigo">Mdc Light Indigo</option>
                                <option value="mira">Mira</option>
                                <option value="nano">Nano</option>
                                <option value="nova">Nova</option>
                                <option value="nova-accent">Nova Accent</option>
                                <option value="nova-alt">Nova Alt</option>
                                <option value="rhea">Rhea</option>
                                <option value="saga-blue">Saga Blue</option>
                                <option value="saga-green">Saga Green</option>
                                <option value="saga-orange">Saga Orange</option>
                                <option value="saga-purple">Saga Purple</option>
                                <option value="soho-dark">Soho Dark</option>
                                <option value="soho-light">Soho Light</option>
                                <option value="tailwind-light">Tailwind Light</option>
                                <option value="vela-blue">Vela Blue</option>
                                <option value="vela-green">Vela Green</option>
                                <option value="vela-orange">Vela Orange</option>
                                <option value="vela-purple">Vela Purple</option>
                                <option value="viva-dark">Viva Dark</option>
                                <option value="viva-light">Viva Light</option>
                            </select>
                        </div>
                    </section>

                    <section ref={fitmeQrRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5>FitMe Self-Registration QR Code</h5>
                        <p className="text-color-secondary mb-3" style={{ maxWidth: '520px' }}>
                            Print and display this QR code at your pharmacy counter. 
                        </p>

                        {departments.length > 0 && (
                            <div className="field" style={{ maxWidth: '320px', marginBottom: '1rem' }}>
                                <label className="font-bold" style={{ display: 'block', marginBottom: '0.4rem' }}>
                                    Branch (optional):
                                </label>
                                <Dropdown
                                    value={selectedDepartmentId}
                                    options={[{ id: null, department: 'All branches (pharmacy-wide QR)' }, ...departments]}
                                    optionLabel="department"
                                    optionValue="id"
                                    onChange={(e) => setSelectedDepartmentId(e.value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        )}

                        <Button
                            icon="pi pi-download"
                            label={qrDownloading ? 'Preparing…' : 'Download QR Code'}
                            className="p-button-outlined"
                            disabled={qrDownloading}
                            onClick={handleDownloadQrCode}
                        />
                    </section>

                    <section ref={approvalsRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5>Approval Settings</h5>
                        <div className="formgrid grid">
                            {[
                                { label: 'Require Discount Approval', key: 'discount_approval' },
                                { label: 'Require Price Override Approval', key: 'price_override_approval' },
                                ].map((setting) => (
                                <div key={setting.key} className="field col-12 md:col-6">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <Checkbox
                                            inputId={setting.key}
                                            checked={settings[setting.key]}
                                            onChange={(e) => handleChange(setting.key, e.checked)}
                                        />
                                        <label htmlFor={setting.key} className="ml-2 mb-0">{setting.label}</label>
                                    </div>
                                </div>
                            ))}
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="require_approval_for_purchases"
                                        checked={!!settings.require_approval_for_purchases}
                                        onChange={(e) => handleChange('require_approval_for_purchases', e.checked)}
                                    />
                                    <label htmlFor="require_approval_for_purchases" className="ml-2 mb-0">
                                        Require Purchase Order Approval
                                    </label>
                                </div>
                            </div>
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="require_delete_move_order_approval"
                                        checked={!!settings.require_delete_move_order_approval}
                                        onChange={(e) => handleChange('require_delete_move_order_approval', e.checked)}
                                    />
                                    <label htmlFor="require_delete_move_order_approval" className="ml-2 mb-0">
                                        Require Delete Order Approval
                                    </label>
                                </div>
                            </div>
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="require_transfer_move_order_approval"
                                        checked={!!settings.require_transfer_move_order_approval}
                                        onChange={(e) => handleChange('require_transfer_move_order_approval', e.checked)}
                                    />
                                    <label htmlFor="require_transfer_move_order_approval" className="ml-2 mb-0">
                                        Require Transfer Order Approval
                                    </label>
                                </div>
                            </div>
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="allow_direct_grn_auto_approve"
                                        checked={!!settings.allow_direct_grn_auto_approve}
                                        onChange={(e) => handleChange('allow_direct_grn_auto_approve', e.checked)}
                                    />
                                    <label htmlFor="allow_direct_grn_auto_approve" className="ml-2 mb-0">
                                        Allow direct GRN auto-approve (for OTC deliveries)
                                    </label>
                                </div>
                            </div>
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="allow_self_approve"
                                        checked={!!settings.allow_self_approve}
                                        onChange={(e) => handleChange('allow_self_approve', e.checked)}
                                    />
                                    <label htmlFor="allow_self_approve" className="ml-2 mb-0">
                                        Allow Self Approval of PO
                                    </label>
                                </div>
                            </div>

                            {/* New toggle: allow exceptional inventory */}
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="allow_exceptional_inventory"
                                        checked={!!settings?.custom_json_settings?.allow_exceptional_inventory}
                                        onChange={(e) => handleChange('allow_exceptional_inventory', e.checked)}
                                    />
                                    <label htmlFor="allow_exceptional_inventory" className="ml-2 mb-0">
                                        Allow Exceptional Inventory (enable adding exceptional batches)
                                    </label>
                                </div>
                            </div>
                        </div>
                    </section>    
                    
                    <section ref={operatorauthRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5>Operator Authentication</h5>

                        <div className="flex align-items-center">
                            <Checkbox
                            checked={!!settings.custom_json_settings.operator_authentication?.enabled}
                            onChange={(e) =>
                                handleOperatorAuthChange('enabled', e.checked)
                            }
                            />
                            <label className="ml-2">
                            Require operator authentication for invoices
                            </label>
                        </div>

                        {settings.custom_json_settings.operator_authentication?.enabled && (
                            <>
                            <div className="field mt-3">
                                <label className="font-bold">Allowed Methods</label>

                                <div className="flex gap-4 mt-2">
                                <div className="flex align-items-center">
                                    <Checkbox
                                    checked={settings.custom_json_settings.operator_authentication.methods.includes('PIN')}
                                    onChange={(e) => {
                                        const m = new Set(settings.custom_json_settings.operator_authentication.methods);
                                        e.checked ? m.add('PIN') : m.delete('PIN');
                                        handleOperatorAuthChange('methods', Array.from(m));
                                    }}
                                    />
                                    <label className="ml-2">PIN</label>
                                </div>

                                <div className="flex align-items-center">
                                    <Checkbox
                                    disabled
                                    checked={settings.custom_json_settings.operator_authentication.methods.includes('QR')}
                                    onChange={(e) => {
                                        const m = new Set(settings.custom_json_settings.operator_authentication.methods);
                                        e.checked ? m.add('QR') : m.delete('QR');
                                        handleOperatorAuthChange('methods', Array.from(m));
                                    }}
                                    />
                                    <label className="ml-2">QR Code</label>
                                </div>
                                </div>
                            </div>

                            <div className="flex align-items-center">
                                <Checkbox
                                checked={!!settings.custom_json_settings.operator_authentication.allow_user_pin_change}
                                onChange={(e) =>
                                    handleOperatorAuthChange('allow_user_pin_change', e.checked)
                                }
                                />
                                <label className="ml-2">
                                Allow users to change their own PIN
                                </label>
                            </div>
                            </>
                        )}
                    </section>

                    <section ref={expiryRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5>Expiry Rules</h5>
                        <div className="field">
                            <div className="field col-12 md:col-6">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Checkbox
                                        inputId="block_expired_batches"
                                        checked={settings.block_expired_batches}
                                        onChange={(e) => handleChange('block_expired_batches', e.checked)}
                                    />
                                    <label htmlFor="block_expired_batches" className="ml-2 mb-0">Block Invoicing for Expired Batches</label>
                                </div>
                            </div>
                            <div className="formgrid grid">
                                    <div className="field col-12 md:col-4">
                                    <label htmlFor="block_days" >Block X Days Before Expiry:</label>
                                    <InputNumber
                                        id="block_days"
                                        value={settings.block_invoice_days_before_expiry}
                                        onValueChange={(e) => handleChange('block_invoice_days_before_expiry', e.value)}
                                        min={0}
                                        showButtons
                                        style={{ width: '100px' }}
                                    />
                                    </div>
                                    <div className="field col-12 md:col-4">
                                    <label htmlFor="expiry_days">Reminder Days Before Expiry:</label>
                                    <InputNumber
                                        id="expiry_days"
                                        value={settings.expiry_reminder_days}
                                        onValueChange={(e) => handleChange('expiry_reminder_days', e.value)}
                                        min={0}
                                        showButtons
                                        style={{ width: '100px' }}
                                    />
                                    </div>
                            </div>
                        </div>
                    </section>
                    
                    <section ref={notificationsRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5>Notification Preferences</h5>
                        <div className="formgrid grid">
                            {[
                                { label: 'Batch Expiry Notification: Admin Only', key: 'notify_expiry_admin_only' },
                                { label: 'Refill Reminder: Admin Only', key: 'notify_refill_admin_only' },
                                { label: 'Reorder Level Reminder: Admin Only', key: 'notify_reorder_admin_only' },
                            ].map((setting) => (
                                <div key={setting.key} className="field col-12 md:col-6">
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <Checkbox
                                            inputId={setting.key}
                                            checked={settings[setting.key]}
                                            onChange={(e) => handleChange(setting.key, e.checked)}
                                        />
                                        <label htmlFor={setting.key} className="ml-2 mb-0">{setting.label}</label>
                                    </div>
                                </div>
                            ))}
                        <div className="field col-12 md:col-4">
                            <label htmlFor="refill_days">Reminder Days Before Refill:</label>
                            <InputNumber
                                id="refill_days"
                                value={settings?.custom_json_settings?.refill_reminder_days ?? 2}
                                onValueChange={(e) => handleChange('refill_reminder_days', e.value)}
                                min={0}
                                showButtons
                                style={{ width: '100px' }}
                            />
                        </div>
                        </div>
                    </section>

                    <section ref={smsRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5 className="mb-1">SMS Notifications</h5>
                        <small className="text-color-secondary">
                            Configure your SMS gateway and control which events send a message.
                        </small>
                        <Divider className="my-3" />
                    
                        {/* ── Credentials ───────────────────────────────────────────────────── */}
                        <h6 className="mb-3">SMS Account</h6>
                        <div className="formgrid grid">
                    
                            <div className="field col-12 md:col-6">
                                <label htmlFor="sms_api_url">
                                    API URL <span className="text-red-500">*</span>
                                </label>
                                <InputText
                                    id="sms_api_url"
                                    value={settings.sms_api_url || ''}
                                    onChange={(e) => handleSmsToggleChange('sms_api_url', e.target.value)}
                                    placeholder="https://sms.provider.com/send"
                                    className={`w-full ${!settings.sms_api_url ? 'p-invalid' : ''}`}
                                />
                            </div>
                    
                            <div className="field col-12 md:col-6">
                                <label htmlFor="sms_sender_name">
                                    Sender Name <span className="text-red-500">*</span>
                                    <small className="text-color-secondary ml-2">(max 11 chars)</small>
                                </label>
                                <InputText
                                    id="sms_sender_name"
                                    value={settings.sms_sender_name || ''}
                                    onChange={(e) => handleSmsToggleChange('sms_sender_name', e.target.value.slice(0, 11))}
                                    placeholder="MYPHARMACY"
                                    className={`w-full ${!settings.sms_sender_name ? 'p-invalid' : ''}`}
                                    maxLength={11}
                                />
                            </div>
                    
                            <div className="field col-12 md:col-6">
                                <label htmlFor="sms_bearer_token">Bearer Token</label>
                                <Password
                                    id="sms_bearer_token"
                                    value={settings.sms_bearer_token || ''}
                                    onChange={(e) => handleSmsToggleChange('sms_bearer_token', e.target.value)}
                                    placeholder="Enter new token to replace existing"
                                    className="w-full"
                                    feedback={false}
                                    toggleMask
                                    inputClassName="w-full"
                                />
                                <small className="text-color-secondary">
                                    Leave unchanged to keep existing token. Type a new value to replace it.
                                </small>
                                <Button
                                    label="Clear Token"
                                    icon="pi pi-times"
                                    className="p-button-text p-button-danger p-button-sm mt-1"
                                    onClick={() => handleSmsToggleChange('sms_bearer_token', '')}
                                    tooltip="Clears the stored bearer token — use if your provider does not require one"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                    
                            <div className="field col-12">
                                <label htmlFor="sms_request_template">
                                    Request Body Template <span className="text-red-500">*</span>
                                </label>
                                <small className="text-color-secondary d-block mb-1">
                                    JSON body sent to your SMS gateway. Use{' '}
                                    <code>{'{to}'}</code>, <code>{'{message}'}</code>, <code>{'{sender}'}</code> as placeholders.
                                </small>
                                <InputTextarea
                                    id="sms_request_template"
                                    value={getSmsSettings().request_template || ''}
                                    onChange={(e) => handleSmsCredentialChange('request_template', e.target.value)}
                                    rows={3}
                                    className={`w-full mt-1 ${!getSmsSettings().request_template ? 'p-invalid' : ''}`}
                                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                    placeholder={'{"to": "{to}", "from": "{sender}", "message": "{message}"}'}
                                />
                                <small className="text-color-secondary">
                                    SMSlenz: <code>{'{"user_id":"xxx","api_key":"yyy","sender_id":"{sender}","contact":"{to}","message":"{message}"}'}</code><br/>
                                    Dialog: <code>{'{"sourceAddress":"{sender}","destinationAddress":"{to}","message":"{message}"}'}</code>
                                </small>
                            </div>
                    
                            <div className="field col-12 mt-1">
                                <Button
                                    label="Send Test SMS"
                                    icon="pi pi-send"
                                    className="p-button-outlined p-button-secondary"
                                    onClick={handleTestSms}
                                    disabled={!settings.sms_api_url || !settings.sms_sender_name || !getSmsSettings().request_template}
                                />
                                <small className="text-color-secondary ml-3">
                                    Sends a test message to a number you specify.
                                </small>
                            </div>
                        </div>
                    
                        {/* ── Notification toggles ──────────────────────────────────────────── */}
                        {(() => {
                            // SMS is considered configured only when all three mandatory fields are filled
                            const smsConfigured = !!(
                                settings.sms_api_url &&
                                settings.sms_sender_name &&
                                getSmsSettings().request_template
                            );
                    
                            // Map each toggle key to its corresponding message template key
                            const NOTIFICATION_ITEMS = [
                                { key: 'sms_notify_invoice_ready',  templateKey: 'invoice_ready',   label: 'Invoice ready for collection',  badge: 'Customer', cls: 'bg-blue-100 text-blue-800' },
                                { key: 'sms_notify_refill_reminder', templateKey: 'refill_reminder', label: 'Refill reminder',               badge: 'Customer', cls: 'bg-blue-100 text-blue-800' },
                                { key: 'sms_notify_rx_pickup_ready', templateKey: 'rx_pickup_ready', label: 'Prescription ready for pickup', badge: 'Customer', cls: 'bg-blue-100 text-blue-800' },
                                { key: 'sms_notify_low_stock',       templateKey: 'low_stock',       label: 'Low stock alert',               badge: 'Internal', cls: 'bg-orange-100 text-orange-800' },
                                { key: 'sms_notify_expiry_alert',    templateKey: 'expiry_alert',    label: 'Expiry alert',                  badge: 'Internal', cls: 'bg-orange-100 text-orange-800' },
                                { key: 'sms_notify_birthday_wish',   templateKey: 'birthday_wish',   label: 'Birthday wish',                 badge: 'Customer', cls: 'bg-blue-100 text-blue-800' },
                            ];
                    
                            const TEMPLATE_META = {
                                invoice_ready:   { label: 'Invoice Ready',             vars: '{customer_name}, {invoice_number}, {org_name}',                    placeholder: 'Dear {customer_name}, your invoice #{invoice_number} is ready for collection at {org_name}.' },
                                refill_reminder: { label: 'Refill Reminder',           vars: '{customer_name}, {refill_date}, {org_name}',                       placeholder: 'Dear {customer_name}, your prescription refill is due on {refill_date}. Please visit {org_name}.' },
                                rx_pickup_ready: { label: 'Prescription Pickup Ready', vars: '{customer_name}, {org_name}',                                      placeholder: 'Dear {customer_name}, your prescription is ready for pickup at {org_name}.' },
                                low_stock:       { label: 'Low Stock Alert',           vars: '{product_name}, {current_qty}, {reorder_level}, {org_name}',       placeholder: 'Low Stock: {product_name} is below reorder level ({current_qty}/{reorder_level}). - {org_name}' },
                                expiry_alert:    { label: 'Expiry Alert',              vars: '{product_name}, {batch_number}, {expiry_date}, {org_name}',        placeholder: 'Expiry Alert: {product_name} (Batch {batch_number}) expires on {expiry_date}. - {org_name}' },
                                birthday_wish:   { label: 'Birthday Wish',              vars: '{customer_name}, {org_name}',                                      placeholder: 'Happy Birthday, {customer_name}! Wishing you good health and happiness, from all of us at {org_name}.' },
                            };
                    
                            return (
                                <>
                                    <Divider className="my-3" />
                                    <div className="flex align-items-center gap-2 mb-3">
                                        <h6 className="mb-0">Notification Events</h6>
                                        {!smsConfigured && (
                                            <span className="border-round px-2 py-1 font-medium bg-red-100 text-red-700"
                                                style={{ fontSize: '0.72rem' }}>
                                                Complete SMS account setup above to enable notifications
                                            </span>
                                        )}
                                    </div>
                    
                                    <div className="formgrid grid">
                                        {NOTIFICATION_ITEMS.map(({ key, templateKey, label, badge, cls }) => {
                                            const isEnabled = !!settings[key];
                                            const meta = TEMPLATE_META[templateKey];
                                            return (
                                                <div key={key} className="col-12">
                                                    {/* Toggle row */}
                                                    <div className="flex align-items-center gap-2 mb-1">
                                                        <Checkbox
                                                            inputId={key}
                                                            checked={isEnabled}
                                                            disabled={!smsConfigured}
                                                            onChange={(e) => handleSmsToggleChange(key, e.checked)}
                                                        />
                                                        <label
                                                            htmlFor={key}
                                                            className={`mb-0 mr-2 ${!smsConfigured ? 'text-color-secondary' : ''}`}
                                                            style={{ cursor: !smsConfigured ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            {label}
                                                        </label>
                                                        <span className={`border-round px-2 py-1 font-medium ${cls}`}
                                                            style={{ fontSize: '0.7rem' }}>
                                                            {badge}
                                                        </span>
                                                    </div>
                    
                                                    {/* Template textarea — only shown when this notification is enabled */}
                                                    {isEnabled && (
                                                        <div className="ml-5 mb-3 mt-1">
                                                            <InputTextarea
                                                                value={getSmsSettings().templates?.[templateKey] || ''}
                                                                onChange={(e) => handleSmsTemplateChange(templateKey, e.target.value)}
                                                                rows={2}
                                                                className="w-full"
                                                                style={{ fontSize: '0.85rem' }}
                                                                placeholder={meta.placeholder}
                                                            />
                                                            <small className="text-color-secondary">
                                                                Leave blank for default. Variables: <code>{meta.vars}</code>
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                    
                                    <small className="text-color-secondary">
                                        <strong>Customer</strong> — sent to the phone number on the customer's profile.{' '}
                                        <strong>Internal</strong> — sent to admin employees' mobile numbers.
                                    </small>
                                </>
                            );
                        })()}
                    </section>

                    <section ref={printRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5 className="mb-2">Receipt Printing</h5>
                            <small className="text-color-secondary">
                                Configure invoice printer, layout, header and footer
                            </small>
                            <Divider className="my-3" />

                        <div className="formgrid grid">
                            <div className="flex align-items-center gap-2 mb-2">
                            <Checkbox
                                checked={!!printing.enabled}
                                onChange={(e) => handleChange('printing.enabled', e.checked)}
                            />
                            <label className="ml-2">Enable Printing</label>
                            </div>
                            <div className="field col-12 md:col-6">
                            <label>Receipt Printer Language</label>
                            <select
                                className="p-inputtext"
                                value={printing.receipt_language}
                                onChange={(e) => handleChange('printing.receipt_language', e.target.value)}
                                style={{ maxWidth: '200px' }}
                            >
                                <option value="escpos">ESC/POS (Recommended)</option>
                                <option value="tspl">TSPL (Advanced)</option>
                            </select>
                            </div>

                            <div className="field col-12 md:col-4">
                            <label>Paper Width (mm)</label>
                            <InputNumber
                                value={printing.paper_width_mm}
                                onValueChange={(e) => handleChange('printing.paper_width_mm', e.value)}
                                min={58}
                                max={110}
                                showButtons
                                style={{ maxWidth: '200px' }}
                            />
                            </div>

                            {printing.receipt_language === 'tspl' && (
                            <>
                                <div className="field col-12 md:col-4">
                                <label>Receipt Height (mm)</label>
                                <InputNumber
                                    value={printing.receipt_height_mm}
                                    onValueChange={(e) => handleChange('printing.receipt_height_mm', e.value)}
                                    min={100}
                                    max={1000}
                                    showButtons
                                />
                                </div>

                                <div className="field col-12 md:col-4">
                                <label>Barcode Type</label>
                                <select
                                    className="p-inputtext w-full"
                                    value={printing.barcode_type}
                                    onChange={(e) => handleChange('printing.barcode_type', e.target.value)}
                                >
                                    <option value="CODE128">Code 128</option>
                                    <option value="CODE39">Code 39</option>
                                </select>
                                </div>
                            </>
                            )}

                            <div className="p-3 border-round surface-border border-1 mt-3">
                            <h6 className="mb-3">Receipt Header Configuration</h6>

                            <div className="grid">
                                {Array.isArray(printing.receipt_header) &&
                                    printing.receipt_header.map((h, idx) => (
                                    <div key={h.key} className="col-12">
                                        <div className="grid align-items-center">

                                        {/* LEFT COLUMN: Checkbox + label */}
                                        <div className="col-8 md:col-9">
                                            <div className="flex align-items-center gap-2">
                                            <Checkbox
                                                checked={h.enabled}
                                                onChange={(e) => {
                                                const updated = [...printing.receipt_header];
                                                updated[idx] = { ...h, enabled: e.checked };
                                                handleChange('printing.receipt_header', updated);
                                                }}
                                            />
                                            <label className="mb-0">
                                                {h.key.replace('_', ' ').toUpperCase()}
                                            </label>
                                            </div>
                                        </div>

                                        {/* RIGHT COLUMN: Order */}
                                        <div className="col-4 md:col-3">
                                            <InputNumber
                                            value={h.order}
                                            onValueChange={(e) => {
                                                const updated = [...printing.receipt_header];
                                                updated[idx] = { ...h, order: e.value };
                                                handleChange('printing.receipt_header', updated);
                                            }}
                                            min={1}
                                            max={10}
                                            showButtons
                                            style={{ width: '80px' }}
                                            />
                                        </div>

                                        </div>
                                    </div>
                                ))}
                                </div>
                            <small className="text-color-secondary">
                                Enable/disable fields and control the order they appear on the invoice.
                            </small>
                            </div>
                        <div className="p-3 border-round surface-border border-1 mt-3">
                            <h6 className="mb-3">Receipt Footer</h6>
                            <textarea
                                className="p-inputtext"
                                rows={4}
                                value={printing.receipt_footer_text}
                                onChange={(e) =>
                                handleChange('printing.receipt_footer_text', e.target.value)
                                }
                                placeholder="Footer message shown at the bottom of the invoice"
                                style={{ width: '400px' }}
                            />
                            <div>
                            <small className="text-color-secondary">
                                Example: Thank you for choosing us!
                            </small>
                            </div>
                        </div>
                        </div>
                    </section>
                    
                    <section ref={printlblRef} className="surface-card p-4 border-round shadow-1 mb-4">
                        <h5 className="mb-2">Purchase Order Barcode Labels</h5>
                            <small className="text-color-secondary">
                               Configure barcode content and label size for PO items
                            </small>

                            <div className="field">
                            <Checkbox
                                checked={!!printing.po_barcode_enabled}
                                onChange={e => handleChange('printing.po_barcode_enabled', e.checked)}
                            />
                            <label className="ml-2">Enable PO Item Barcode Printing</label>
                            </div>
                            <div className="formgrid grid">
                            <div className="field col-12 md:col-4">
                                <label>Barcode Value</label>
                                <select
                                className="p-inputtext"
                                value={printing.po_barcode_value}
                                onChange={e => handleChange('printing.po_barcode_value', e.target.value)}
                                style={{ maxWidth: '200px' }}
                                >
                                <option value="batch_number">Batch Number</option>
                                <option value="product_code">Product Code</option>
                                </select>
                            </div>
                            <div className="field col-12 md:col-4">
                                <label>Copies per Item</label>
                                <InputNumber
                                value={printing.po_barcode_copies}
                                min={1}
                                max={10}
                                onValueChange={e => handleChange('printing.po_barcode_copies', e.value)}
                                style={{ maxWidth: '200px' }}
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label>Label Width (mm)</label>
                                <InputNumber
                                value={printing.label_width_mm}
                                min={30}
                                max={100}
                                onValueChange={e => handleChange('printing.label_width_mm', e.value)}
                                style={{ maxWidth: '200px' }}
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label>Label Height (mm)</label>
                                <InputNumber
                                value={printing.label_height_mm}
                                min={20}
                                max={80}
                                onValueChange={e => handleChange('printing.label_height_mm', e.value)}
                                style={{ maxWidth: '200px' }}
                                />
                            </div>
                            <div className="field col-12 md:col-4">
                                <label>Label Left Margin (mm)</label>
                                <InputNumber
                                value={printing.label_margin_mm}
                                min={0}
                                max={10}
                                onValueChange={e => handleChange('printing.label_margin_mm', e.value)}
                                style={{ maxWidth: '200px' }}
                                />
                            </div>
                            </div>
                            </section>
                        </div>   
                       
                </div>
    );
};

export default OrganizationSettingsPage;